import { useState, type FormEvent } from "react";
import { Filter, FilterX, Search, X } from "lucide-react";
import { FLOW_TEXT_INPUT_BASE } from "../../styles/twDesignPrimitives";
import { PropertiesCodeSearchField, SEARCH_SUBMIT_BUTTON } from "./PropertiesCodeSearchField";
import { PropertiesListFiltersModal } from "./PropertiesListFiltersModal";
import {
  countAdvancedPropertyListFilters,
  emptyPropertyListAdvancedFilters,
  type PropertyListAdvancedFilters,
  type PropertyListFilters,
} from "./propertiesListQuery";

const FILTER_ACTION_BUTTON =
  "inline-flex h-[38px] flex-1 shrink-0 items-center justify-center gap-2 rounded-md border border-solid border-[#E2E8F0] bg-white px-3 text-sm font-medium text-slate-600 transition hover:border-[#CBD5E0] hover:bg-slate-50 md:flex-none";

type Props = {
  searchTerm: string;
  filters: PropertyListFilters;
  showConsultantFilter: boolean;
  onSearchChange: (value: string) => void;
  /** "Buscar" / Enter: searches right away instead of waiting for the typing pause. */
  onSearchSubmit: () => void;
  onApplyAdvancedFilters: (filters: PropertyListAdvancedFilters) => void;
};

export function PropertiesListFilters({
  searchTerm,
  filters,
  showConsultantFilter,
  onSearchChange,
  onSearchSubmit,
  onApplyAdvancedFilters,
}: Props) {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const activeCount = countAdvancedPropertyListFilters(filters);

  function handleSearchSubmit(event: FormEvent) {
    event.preventDefault();
    onSearchSubmit();
  }

  return (
    <div className="mb-4 mt-4 flex w-full flex-col gap-3 md:mb-6">
      <div className="flex w-full flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
        <PropertiesCodeSearchField />

        <form
          onSubmit={handleSearchSubmit}
          role="search"
          className="flex min-w-0 flex-1 flex-col gap-1 text-left text-xs font-medium text-slate-600 md:max-w-xl"
        >
          <label htmlFor="property-list-search">Busca</label>
          <div className="flex w-full gap-2">
            <div className="relative min-w-0 flex-1">
              <input
                id="property-list-search"
                type="search"
                enterKeyHint="search"
                className={`${FLOW_TEXT_INPUT_BASE} h-[38px] !pr-9 !font-[inherit] [&::-webkit-search-cancel-button]:hidden`}
                value={searchTerm}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Bairro, rua, e-mail do proprietário, andar"
              />
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => onSearchChange("")}
                  aria-label="Limpar busca"
                  className="absolute right-1 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md border-0 bg-transparent text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
            </div>
            <button type="submit" className={SEARCH_SUBMIT_BUTTON}>
              <Search className="h-4 w-4" aria-hidden />
              Buscar
            </button>
          </div>
        </form>

        <div className="flex w-full gap-2 md:w-auto">
          <button
            type="button"
            onClick={() => setIsFiltersOpen(true)}
            className={FILTER_ACTION_BUTTON}
          >
            <Filter className="h-4 w-4" aria-hidden />
            Filtros
            {activeCount > 0 ? (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-orange-500 px-1.5 text-[11px] font-semibold text-white">
                {activeCount}
              </span>
            ) : null}
          </button>

          {activeCount > 0 ? (
            <button
              type="button"
              onClick={() =>
                onApplyAdvancedFilters(emptyPropertyListAdvancedFilters())
              }
              className={FILTER_ACTION_BUTTON}
            >
              <FilterX className="h-4 w-4" aria-hidden />
              Limpar filtros
            </button>
          ) : null}
        </div>
      </div>

      <p className="m-0 text-xs text-slate-500">
        A busca atualiza enquanto você digita, ou use o botão Buscar. Os demais
        filtros ficam no botão Filtros.
      </p>

      <PropertiesListFiltersModal
        open={isFiltersOpen}
        filters={filters}
        showConsultantFilter={showConsultantFilter}
        onClose={() => setIsFiltersOpen(false)}
        onApply={onApplyAdvancedFilters}
      />
    </div>
  );
}
